import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { SelectableTag } from 'components/Form/SelectableTag/SelectableTag';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Alert } from 'components/UI/Alert/Alert';
import { useMarketplace } from 'hooks/useMarketplace';
import { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    getOnlineBookingDaycareAvailabilityByDate,
    getOnlineBookingDaycareAvailabilityLoading,
    getOnlineBookingDaycareDates,
    getOnlineBookingDaycareItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    DaycareAvailabilityDate,
    DaycareAvailabilityDropOffSlot,
    DaycareAvailabilityPickUpSlot
} from 'types/Availability';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingStep, OnlineBookingTime } from 'types/OnlineBooking';
import {
    assignDaycareUnitsForPetsAcrossDates,
    convertMomentToOnlineBookingTime,
    getSharedDaycareDropOffSlots,
    getSharedDaycarePickUpSlots
} from 'utils/daycareUtils';
import classes from './OnlineBookingDaycareTimePicker.module.scss';

const dateKey = (date: Moment): string => date.format('YYYY-MM-DD');

const isSameTime = (time?: OnlineBookingTime, other?: OnlineBookingTime) => {
    if (!time || !other) return false;

    return other.hour === time.hour && other.minute === time.minute;
};

export const OnlineBookingDaycareTimePicker: React.FC = () => {
    const marketplace = useMarketplace();

    const [assignmentFailed, setAssignmentFailed] = React.useState(false);

    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        MarketplaceServiceCategory.DAYCARE
    ]);
    const bookingItems = useSelector(getOnlineBookingDaycareItems);
    const selectedDates = useSelector(getOnlineBookingDaycareDates);
    const availabilityByDate = useSelector(getOnlineBookingDaycareAvailabilityByDate);
    const daycareAvailabilityLoading = useSelector(getOnlineBookingDaycareAvailabilityLoading);

    const selectedDropOffTime = bookingItems[0]?.dropOffTime;
    const selectedPickUpTime = bookingItems[0]?.pickUpTime;

    const availabilities = React.useMemo<Array<DaycareAvailabilityDate>>(() => {
        const loaded = selectedDates
            .map(date => availabilityByDate[dateKey(date)])
            .filter((availability): availability is DaycareAvailabilityDate => !!availability);

        return loaded.length === selectedDates.length ? loaded : [];
    }, [availabilityByDate, selectedDates]);

    const dropOffSlots = React.useMemo(
        () => getSharedDaycareDropOffSlots(availabilities),
        [availabilities]
    );

    const pickUpSlots = React.useMemo(() => {
        if (!selectedDropOffTime) return [];

        return getSharedDaycarePickUpSlots(availabilities, selectedDropOffTime);
    }, [availabilities, selectedDropOffTime]);

    const isValid = React.useMemo(() => {
        return (
            selectedDropOffTime !== undefined &&
            selectedPickUpTime !== undefined &&
            bookingItems.every(
                item =>
                    (item.days?.length ?? 0) > 0 &&
                    item.days?.every(day => day.roomUnitId !== undefined)
            )
        );
    }, [bookingItems, selectedDropOffTime, selectedPickUpTime]);

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    const dropOffChangeHandler = (slot: DaycareAvailabilityDropOffSlot) => {
        setAssignmentFailed(false);

        const dropOffTime = convertMomentToOnlineBookingTime(slot.datetime);

        const updatedItems = bookingItems.map(item => ({
            ...item,
            dropOffTime,
            pickUpTime: undefined,
            days: (item.days ?? []).map(day => ({ date: day.date, roomUnitId: undefined }))
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const pickUpChangeHandler = (slot: DaycareAvailabilityPickUpSlot) => {
        if (!selectedDropOffTime) return;

        const pickUpTime = convertMomentToOnlineBookingTime(slot.datetime);

        const assignmentsByDate = assignDaycareUnitsForPetsAcrossDates(
            availabilities,
            selectedDropOffTime,
            pickUpTime,
            bookingItems.map(item => item.pet)
        );

        if (!assignmentsByDate) {
            setAssignmentFailed(true);

            return;
        }

        setAssignmentFailed(false);

        const updatedItems = bookingItems.map((item, itemIndex) => ({
            ...item,
            pickUpTime,
            days: (item.days ?? []).map(day => ({
                date: day.date,
                roomUnitId: assignmentsByDate[dateKey(day.date)]?.[itemIndex]
            }))
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const submitHandler = () => {
        if (addonServices.length > 0) {
            return dispatch(navigateToStep(OnlineBookingStep.AddonList));
        }

        dispatch(navigateToStep(OnlineBookingStep.Instructions));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h2 underlined w-full">Select hours</h1>

                <div className={classes.timeGroup}>
                    <p className={classes.timeGroupLabel}>Drop-off</p>

                    <div className={classes.timeGrid}>
                        {dropOffSlots.map(slot => {
                            const dropOffTime = convertMomentToOnlineBookingTime(slot.datetime);

                            return (
                                <SelectableTag
                                    key={slot.datetime.toISOString()}
                                    className={classes.timeTag}
                                    selected={isSameTime(selectedDropOffTime, dropOffTime)}
                                    onClick={() => dropOffChangeHandler(slot)}
                                >
                                    {dropOffTime.label}
                                </SelectableTag>
                            );
                        })}
                    </div>
                </div>

                {selectedDropOffTime && (
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Pick-up</p>

                        <div className={classes.timeGrid}>
                            {pickUpSlots.map(slot => {
                                const pickUpTime = convertMomentToOnlineBookingTime(slot.datetime);

                                return (
                                    <SelectableTag
                                        key={slot.datetime.toISOString()}
                                        className={classes.timeTag}
                                        selected={isSameTime(selectedPickUpTime, pickUpTime)}
                                        onClick={() => pickUpChangeHandler(slot)}
                                    >
                                        {pickUpTime.label}
                                    </SelectableTag>
                                );
                            })}
                        </div>
                    </div>
                )}

                {assignmentFailed && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        className="mt-4"
                        text="This time can't fit all pets on every selected day. Try another time or remove a day."
                    />
                )}
            </LeftColumn>
            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        loading={daycareAvailabilityLoading}
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
